import { apiClient } from './apiClient';
import { User } from '@/types';
import { API_BASE_URL } from '@/config/environment';

class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'user_data';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly REFRESH_TOKEN_EXPIRES_KEY = 'refresh_token_expires_at';

  // Get stored token
  async getToken(): Promise<string | null> {
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (!token) return null;
    const normalized = token.trim();
    if (normalized === 'undefined' || normalized === 'null' || normalized === '') return null;
    return normalized;
  }

  // Get refresh token
  async getRefreshToken(): Promise<string | null> {
    const token = localStorage.getItem(this.REFRESH_TOKEN_KEY);
    if (!token) return null;
    const normalized = token.trim();
    if (normalized === 'undefined' || normalized === 'null' || normalized === '') return null;
    return normalized;
  }

  // Set tokens
  async setTokens(accessToken: string, refreshToken: string, refreshTokenExpiresAt: string): Promise<void> {
    localStorage.setItem(this.TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(this.REFRESH_TOKEN_EXPIRES_KEY, refreshTokenExpiresAt);
  }

  // Clear tokens
  async clearTokens(): Promise<void> {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_EXPIRES_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  // Login
  async login(email: string, password: string): Promise<User> {
    try {
      // Use form-encoded data for login (matching React Native frontend)
      const form = `username=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`;

      const response = await fetch(`${API_BASE_URL}/login/access-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: form,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Login failed');
      }

      const data = await response.json();
      await this.setTokens(data.access_token, data.refresh_token, data.refresh_token_expires_at);

      // Get current user data
      const userResponse = await apiClient.get<User>('/users/me');
      localStorage.setItem(this.USER_KEY, JSON.stringify(userResponse));

      return userResponse;
    } catch (error: any) {
      if (error.message && error.message.includes('Wrong email or password')) {
        throw new Error('Wrong email or password, please check your details and try again.');
      } else if (error.message && (error.message.includes('Network Error') || error.message.includes('offline'))) {
        throw new Error('Offline and cannot login.');
      } else {
        throw new Error(error.message || 'Login failed.');
      }
    }
  }

  // Get current user
  async getCurrentUser(): Promise<User | null> {
    const json = localStorage.getItem(this.USER_KEY);
    return json ? (JSON.parse(json) as User) : null;
  }

  // Fetch fresh user data from backend
  async fetchCurrentUser(): Promise<User | null> {
    const token = await this.getToken();
    if (!token) return null;
    
    try {
      const user = await apiClient.get<User>('/users/me');
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
      return user;
    } catch (error) {
      console.error('Error fetching current user:', error);
      return null;
    }
  }

  // Sign out
  async signOut(): Promise<void> {
    try {
      const token = await this.getToken();
      if (token) {
        const refreshToken = await this.getRefreshToken();
        await apiClient.post('/auth/logout', { refresh_token: refreshToken });
      }
    } catch (error) {
      console.warn('Logout endpoint failed, continuing with local cleanup:', error);
    } finally {
      await this.clearTokens();
    }
  }

  // Forgot password
  async forgotPassword(email: string): Promise<void> {
    try {
      await apiClient.post('/auth/forgot-password', { email });
    } catch (error) {
      console.error('Error sending forgot password request:', error);
      throw error;
    }
  }

  // Perform token refresh. Delegates to apiClient's mutex-protected refresh so the
  // interceptor (on a 401) and this monitor never present the rotating refresh token twice at once.
  async performTokenRefresh(): Promise<boolean> {
    return apiClient.refreshToken();
  }
}

export const authService = new AuthService();
